import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { apiGet, apiPost, setPlatformApiToken } from "../lib/api";

export type PlatformBootstrap = {
  user: { user_id: string; role: "manager" | "viewer"; telegram_chat_id?: string | null };
  permissions: string[];
  summary: {
    tenants_total: number;
    tenants_active: number;
    tenants_suspended: number;
    tenants_onboarding: number;
  };
};

type PlatformAuthContextValue = {
  token: string;
  bootstrap: PlatformBootstrap | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  can: (permission: string) => boolean;
};

const PlatformAuthContext = createContext<PlatformAuthContextValue | undefined>(undefined);
const TOKEN_KEY = "v3_platform_token";

export function PlatformAuthProvider({ children }: { children: React.ReactNode }) {
  const [token, setToken] = useState<string>(localStorage.getItem(TOKEN_KEY) ?? "");
  const [bootstrap, setBootstrap] = useState<PlatformBootstrap | null>(null);
  const [isLoading, setLoading] = useState<boolean>(() => !!localStorage.getItem(TOKEN_KEY));

  useEffect(() => {
    setPlatformApiToken(token);
    if (!token) {
      setBootstrap(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    apiGet<PlatformBootstrap>("/v3/sys/bootstrap", { auth: "platform" })
      .then((data) => setBootstrap(data))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setBootstrap(null);
      })
      .finally(() => setLoading(false));
  }, [token]);

  const value = useMemo<PlatformAuthContextValue>(
    () => ({
      token,
      bootstrap,
      isLoading,
      async login(email: string, password: string) {
        const data = await apiPost<{ access_token: string }>("/v3/sys/auth/login", { email, password }, { auth: "none" });
        localStorage.setItem(TOKEN_KEY, data.access_token);
        setToken(data.access_token);
      },
      logout() {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setBootstrap(null);
      },
      can(permission: string) {
        return !!bootstrap?.permissions.includes(permission);
      },
    }),
    [token, bootstrap, isLoading]
  );

  return <PlatformAuthContext.Provider value={value}>{children}</PlatformAuthContext.Provider>;
}

export function usePlatformAuth() {
  const ctx = useContext(PlatformAuthContext);
  if (!ctx) {
    throw new Error("Platform auth context missing");
  }
  return ctx;
}

